import { todayStr } from "../lib/date";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { getPublicSchools } from "../lib/api";

const CATEGORIES = [
  { key: "participation_level", label: "Participation", desc: "How actively did you participate today?",
    rubric: { 1: "Did not participate", 2: "Participated in some of the activity", 3: "Participated in most of the activity", 4: "Fully participated in the activity" } },
  { key: "engagement", label: "Engagement", desc: "How focused and interested were you?",
    rubric: { 1: "Not focused or interested", 2: "Somewhat focused, needed frequent reminders", 3: "Mostly focused and interested", 4: "Fully focused and interested throughout" } },
  { key: "communication", label: "Communication", desc: "How well did you communicate with others?",
    rubric: { 1: "Did not communicate appropriately", 2: "Sometimes communicated appropriately", 3: "Usually communicated appropriately", 4: "Communicated appropriately throughout" } },
  { key: "behavior", label: "Behavior", desc: "How well did you follow expectations?",
    rubric: { 1: "Did not follow expectations", 2: "Sometimes followed expectations", 3: "Usually followed expectations", 4: "Consistently followed all expectations" } },
  { key: "attention", label: "Attention", desc: "How well did you stay on task?",
    rubric: { 1: "Could not stay on task", 2: "Stayed on task some of the time", 3: "Stayed on task most of the time", 4: "Stayed on task the entire time" } },
  { key: "work_completion", label: "Work Completion", desc: "How much of your work did you finish?",
    rubric: { 1: "Completed 0-10% of work", 2: "Completed 10-50% of work", 3: "Completed 50-90% of work", 4: "Completed 90-100% of work" } },
];

const SCORE_LABELS = { 1: "Needs Improvement", 2: "Developing", 3: "Meeting Expectations", 4: "Exceeding" };
const SCORE_COLORS = { 1: "bg-red-500", 2: "bg-orange-400", 3: "bg-blue-500", 4: "bg-green-500" };

function ScoreButton({ value, selected, onClick }) {
  const isSelected = selected === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`score-btn-${value}`}
      className={`w-12 h-12 rounded-lg text-lg font-bold transition-all ${
        isSelected
          ? `${SCORE_COLORS[value]} text-white scale-110 shadow-lg`
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {value}
    </button>
  );
}

// Fallback block windows used only until the school's real block schedule loads.
function getCurrentBlock() {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes < 10 * 60) return "Block 1";
  if (minutes < 11 * 60) return "Block 2";
  if (minutes < 12 * 60) return "Block 3";
  if (minutes < 13 * 60) return "Block 4";
  if (minutes < 14 * 60) return "Block 5";
  return "Block 6";
}

const timeToMin = (t) => {
  const [h, m] = (t || "").split(":").map(Number);
  return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
};

// Default to the block that is ABOUT TO END or JUST ENDED — i.e. the latest block
// whose start time has already passed. Never jumps ahead to the next/upcoming block.
function defaultBlockFromSchedule(blocksArr) {
  if (!blocksArr || blocksArr.length === 0) return getCurrentBlock();
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  let chosen = null;
  for (const b of blocksArr) {
    if (timeToMin(b.start_time) <= minutes) chosen = b;
  }
  return (chosen || blocksArr[0]).name;
}

export default function SelfAssessmentPage() {
  const location = useLocation();
  const [step, setStep] = useState("login"); // login, assess, done
  const [accessCode, setAccessCode] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState({});
  const [rubricOpen, setRubricOpen] = useState(true);
  const [block, setBlock] = useState(getCurrentBlock());
  const [className, setClassName] = useState("");
  const [notes, setNotes] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [otherClassName, setOtherClassName] = useState("");
  const [scheduleClasses, setScheduleClasses] = useState([]);
  const [scheduleClassChoice, setScheduleClassChoice] = useState("");
  const [history, setHistory] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const today = todayStr();

  // Load the public school directory for the picker
  useEffect(() => {
    getPublicSchools().then(res => setSchools(Array.isArray(res.data) ? [...res.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")) : [])).catch(() => setSchools([]));
  }, []);

  // Fetch core classes (public endpoint), scoped to the chosen school
  useEffect(() => {
    if (!schoolId) return;
    api.get(`/core-classes/public?school_id=${schoolId}`).then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setSubjectOptions(list.includes("Other") ? list : [...list, "Other"]);
    }).catch(() => {
      setSubjectOptions(["Math", "Science", "English", "Social Studies", "Movement", "Community Connections", "Other"]);
    });
  }, [schoolId]);

  // Day-specific master-schedule class names (1:1s excluded) for the "Other" picker.
  useEffect(() => {
    if (!schoolId) return;
    api.get(`/self-assessment/day-classes?date=${today}&school_id=${schoolId}`)
      .then(res => setScheduleClasses(Array.isArray(res.data) ? res.data : []))
      .catch(() => setScheduleClasses([]));
  }, [schoolId]);

  // Fetch the school's real block schedule and default to the block ending/just ended.
  useEffect(() => {
    if (!schoolId) return;
    api.get(`/self-assessment/blocks?school_id=${schoolId}`).then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setBlockOptions(list);
      if (list.length > 0) setBlock(defaultBlockFromSchedule(list));
    }).catch(() => setBlockOptions([]));
  }, [schoolId]);

  // Auto-login if navigated from login page with code + school in state
  useEffect(() => {
    if (location.state?.code && location.state?.studentInfo) {
      setAccessCode(location.state.code);
      setStudentInfo(location.state.studentInfo);
      const sid = location.state.schoolId || "";
      setSchoolId(sid);
      setScores({});
      setClassName("");
      setScheduleClassChoice(""); setOtherClassName("");
      setBlock(defaultBlockFromSchedule(blockOptions));
      setNotes("");
      // Load history
      api.get(`/self-assessment/history?access_code=${location.state.code}&school_id=${sid}`).then(res => setHistory(res.data)).catch(() => {});
      setStep("assess");
    }
  }, []);

  const handleLogin = async () => {
    if (!schoolId) { toast.error("Please select your school"); return; }
    if (!accessCode.trim()) { toast.error("Enter your access code"); return; }
    setLoading(true);
    try {
      const res = await api.post("/self-assessment/login", { access_code: accessCode.trim().toUpperCase(), school_id: schoolId });
      setStudentInfo(res.data);
      setScores({});
      setClassName("");
      setScheduleClassChoice(""); setOtherClassName("");
      setBlock(defaultBlockFromSchedule(blockOptions));
      setNotes("");
      // Load history
      try {
        const histRes = await api.get(`/self-assessment/history?access_code=${accessCode.trim().toUpperCase()}&school_id=${schoolId}`);
        setHistory(histRes.data);
      } catch (e) {
        console.error("Failed to load self-assessment history:", e);
      }
      setStep("assess");
      toast.success(`Welcome, ${res.data.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid access code. Please try again.");
      setStudentInfo(null);
      setAccessCode("");
      setStep("login");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    const hasScores = CATEGORIES.some(c => scores[c.key] != null);
    if (!hasScores) { toast.error("Please rate at least one category"); return; }
    setLoading(true);
    try {
      await api.post("/self-assessment/submit", {
        access_code: accessCode.trim().toUpperCase(),
        school_id: schoolId,
        date: today,
        block,
        class_name: className === "Other"
          ? (scheduleClassChoice && scheduleClassChoice !== "__other__" ? scheduleClassChoice : (otherClassName.trim() || "Other"))
          : className,
        ...scores,
        notes,
      });
      setStep("done");
      toast.success("Self-assessment submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setLoading(false);
  };

  // Start another assessment for the SAME student without re-entering the code.
  // Resets the form to a fresh block/class and refreshes recent history.
  const startAnother = () => {
    setScores({});
    setClassName("");
    setScheduleClassChoice("");
    setOtherClassName("");
    setBlock(defaultBlockFromSchedule(blockOptions));
    setNotes("");
    api.get(`/self-assessment/history?access_code=${accessCode.trim().toUpperCase()}&school_id=${schoolId}`)
      .then(res => setHistory(res.data)).catch(() => {});
    setStep("assess");
  };

  // LOGIN SCREEN
  if (step === "login") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#00305E] to-[#004d99] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl" data-testid="self-assessment-login">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#00305E] flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <CardTitle className="text-2xl">Student Self-Assessment</CardTitle>
            <CardDescription className="text-base mt-1">Enter your access code to rate yourself today</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Your School</Label>
              <Select value={schoolId} onValueChange={setSchoolId}>
                <SelectTrigger className="h-12" data-testid="self-school-select">
                  <SelectValue placeholder="Select your school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`self-school-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Access Code</Label>
              <Input
                value={accessCode}
                onChange={e => setAccessCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="e.g., ABC123"
                className="text-center text-2xl tracking-[0.3em] font-mono h-14 uppercase"
                maxLength={6}
                data-testid="access-code-input"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck="false"
                inputMode="text"
              />
            </div>
            <Button
              onClick={handleLogin}
              disabled={loading || !accessCode.trim() || !schoolId}
              className="w-full h-12 text-lg bg-[#00305E] hover:bg-[#00305E]/90"
              data-testid="login-submit-btn"
            >
              {loading ? "Checking..." : "Start Assessment"}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Don't have a code? Ask your teacher.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // DONE SCREEN
  if (step === "done") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#00305E] to-[#004d99] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl text-center" data-testid="self-assessment-done">
          <CardContent className="pt-8 pb-8 space-y-4">
            <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[#00305E]">Great job, {studentInfo?.name}!</h2>
            <p className="text-muted-foreground">Your self-assessment for {new Date(today + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} has been saved.</p>
            <p className="text-sm text-muted-foreground">Have another class to rate? Submit another one below.</p>
            <div className="pt-4 space-y-3">
              <Button onClick={startAnother} className="w-full h-12 text-lg bg-[#00305E] hover:bg-[#00305E]/90" data-testid="submit-another-btn">
                Submit Another
              </Button>
              <Button variant="outline" onClick={() => { setStep("login"); setAccessCode(""); setStudentInfo(null); setScores({}); setClassName(""); setScheduleClassChoice(""); setOtherClassName(""); setBlock(defaultBlockFromSchedule(blockOptions)); setNotes(""); }} className="w-full" data-testid="done-btn">
                I'm Done
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ASSESSMENT SCREEN
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#00305E] to-[#004d99] p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between text-white">
          <div>
            <h1 className="text-2xl font-bold">{studentInfo?.name}'s Self-Assessment</h1>
            <p className="text-blue-200 text-sm">
              {new Date(today + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => { setStep("login"); setAccessCode(""); }}>
            Log Out
          </Button>
        </div>

        {/* Block & Class Selection */}
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Block</Label>
                <Select value={block} onValueChange={setBlock}>
                  <SelectTrigger data-testid="self-block-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(blockOptions.length > 0 ? blockOptions.map(b => b.name) : ["Block 1","Block 2","Block 3","Block 4","Block 5","Block 6"]).map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Class</Label>
                <Select value={className} onValueChange={(v) => { setClassName(v); if (v !== "Other") { setScheduleClassChoice(""); setOtherClassName(""); } else { setScheduleClassChoice(scheduleClasses.length ? "" : "__other__"); } }}>
                  <SelectTrigger data-testid="self-class-select"><SelectValue placeholder="Select class..." /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {className === "Other" && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <Label className="text-xs font-semibold text-amber-700">Pick your class from today's schedule</Label>
                    <Select value={scheduleClassChoice} onValueChange={(v) => { setScheduleClassChoice(v); if (v !== "__other__") setOtherClassName(""); }}>
                      <SelectTrigger className="bg-white" data-testid="self-schedule-class-select">
                        <SelectValue placeholder={scheduleClasses.length ? "Select a scheduled class..." : "No classes scheduled — choose Other"} />
                      </SelectTrigger>
                      <SelectContent>
                        {scheduleClasses.map(s => <SelectItem key={s} value={s} data-testid={`self-schedule-class-${s}`}>{s}</SelectItem>)}
                        <SelectItem value="__other__" data-testid="self-schedule-class-other">Other (type it in)</SelectItem>
                      </SelectContent>
                    </Select>
                    {scheduleClassChoice === "__other__" && (
                      <Input value={otherClassName} onChange={(e) => setOtherClassName(e.target.value)} placeholder="Enter class name..." autoComplete="off" autoCorrect="off" spellCheck="false" className="bg-white" data-testid="self-other-class-input" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rubric Reference */}
        <Card data-testid="rubric-reference">
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setRubricOpen(!rubricOpen)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">What do the numbers mean?</CardTitle>
              <span className="text-muted-foreground text-sm">{rubricOpen ? "Hide" : "Show"}</span>
            </div>
          </CardHeader>
          {rubricOpen && (
            <CardContent className="pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="rubric-table">
                  <thead>
                    <tr className="bg-[#00305E] text-white">
                      <th className="px-3 py-2 text-left font-semibold border border-[#00305E]">Category</th>
                      <th className="px-3 py-2 text-center font-semibold border border-[#00305E] w-[22%]">1</th>
                      <th className="px-3 py-2 text-center font-semibold border border-[#00305E] w-[22%]">2</th>
                      <th className="px-3 py-2 text-center font-semibold border border-[#00305E] w-[22%]">3</th>
                      <th className="px-3 py-2 text-center font-semibold border border-[#00305E] w-[22%]">4</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CATEGORIES.map((cat, i) => (
                      <tr key={cat.key} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                        <td className="px-3 py-2 font-medium border border-gray-200">{cat.label}</td>
                        <td className="px-3 py-2 text-center text-xs border border-gray-200">{cat.rubric[1]}</td>
                        <td className="px-3 py-2 text-center text-xs border border-gray-200">{cat.rubric[2]}</td>
                        <td className="px-3 py-2 text-center text-xs border border-gray-200">{cat.rubric[3]}</td>
                        <td className="px-3 py-2 text-center text-xs border border-gray-200">{cat.rubric[4]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Rating Cards */}
        <Card data-testid="self-assessment-form">
          <CardHeader>
            <CardTitle>How did you do today?</CardTitle>
            <CardDescription>Rate yourself 1-4 for each category. Be honest — this helps you and your teacher!</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {CATEGORIES.map(cat => (
              <div key={cat.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">{cat.label}</Label>
                    <p className="text-xs text-muted-foreground">{cat.desc}</p>
                  </div>
                  {scores[cat.key] && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${SCORE_COLORS[scores[cat.key]]}`}>
                      {SCORE_LABELS[scores[cat.key]]}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  {[1, 2, 3, 4].map(v => (
                    <ScoreButton key={v} value={v} selected={scores[cat.key]}
                      onClick={(val) => setScores(p => ({ ...p, [cat.key]: p[cat.key] === val ? null : val }))} />
                  ))}
                </div>
                {scores[cat.key] && (
                  <p className="text-xs text-muted-foreground italic" data-testid={`rubric-desc-${cat.key}`}>
                    {cat.rubric[scores[cat.key]]}
                  </p>
                )}
              </div>
            ))}

            <div className="space-y-2 pt-2">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything else you want to share about today?" data-testid="self-notes-input" autoComplete="off" autoCorrect="off" spellCheck="false" />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full h-12 text-lg bg-[#00305E] hover:bg-[#00305E]/90"
              data-testid="submit-self-assessment-btn"
            >
              {loading ? "Submitting..." : "Submit Self-Assessment"}
            </Button>
          </CardContent>
        </Card>

        {/* Previous Self-Assessments */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Recent Self-Assessments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.slice(0, 5).map(h => (
                  <div key={h.id} className="flex items-center justify-between p-2 rounded border text-sm">
                    <span>{new Date(h.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {h.block}</span>
                    <div className="flex gap-1">
                      {CATEGORIES.map(c => h[c.key] != null && (
                        <span key={c.key} className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white ${SCORE_COLORS[h[c.key]]}`}>
                          {h[c.key]}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

